import Task from './Task';
import { createError } from '../error';

const getRecapResponseSetUrl = '/partial_recap_response_set';
const createPartialResponseSetUrl = '/partial_recap_response_set';
const uploadRecapResponseFile = '/recap_response_set/upload';

class PartialRecapResponseSaveTask extends Task {
  constructor($q, APIService, DataSyncService, LogService, RecapResponseService, DocumentService) {
    super();
    this.$q = $q;
    this.APIService = APIService;
    this.DataSyncService = DataSyncService;
    this.RecapResponseService = RecapResponseService;
    this.LogService = LogService;
    this.DocumentService = DocumentService;
    this.recapResponse = null;
    this._recapResponseData = null;
    this._allDocumentDataToUpload = [];
    this._trackDocumentIdsForFieldResponses = {};
  }

  /**
   * Public properties
   */

  set serializedDocumentsToUpload(docs) {
    this._serializedDocumentsToUpload = docs;
  }

  get serializedDocumentsToUpload() {
    return this._serializedDocumentsToUpload;
  }

  set recapResponseData(data) {
    this._recapResponseData = data;
  }

  get recapResponseData() {
    return this._recapResponseData;
  }

  get canExecute() {
    return this.APIService.isOnline;
  }

  get updatingObjectTypesAndIds() {
    const updating = [];
    if (this.recapResponseData) {
      updating.push(['RecapResponse', this.recapResponseData.id]);
    }
    return updating;
  }

  /**
   * Public methods
   */

  populate(data) {
    this.staffUserId = data.staffUserId;
    this.serializedDocumentsToUpload = data.serializedDocumentsToUpload || [];
    this.recapResponseData = data.recapResponseData;
  }

  serialize() {
    return {
      staffUserId: this.staffUserId,
      serializedDocumentsToUpload: this.serializedDocumentsToUpload,
      recapResponseData: this.recapResponseData,
      ...super.serialize(),
    };
  }

  setRecapResponse(recapResponse) {
    this.staffUserId = recapResponse.staffing_users_id;
    // Serialize documents for upload
    this.serializedDocumentsToUpload = this._serializeDocumentsForUpload(recapResponse);
    // Serialize recap response
    this.recapResponseData = this.RecapResponseService.serialize(recapResponse);
    // Keep track of raw recap response
    this.recapResponse = recapResponse;
  }

  execute() {
    this.LogService.info("PartialRecapResponseSaveTask.execute()");

    const uploadedDocumentIdsByFieldResponseId = {};
    const uploadedDocumentUuidsByFieldResponseId = {};
    const documentUploadPromises = this.serializedDocumentsToUpload
      .map(
        docInfo => this._uploadSerializedDocument(docInfo)
          .catch(err => {
            this.LogService.error("Error uploading document; swallowing. Document:", docInfo, "error:", err);
            return this.$q.reject(err);
          })
          .then(updatedDocInfo => {
            this.LogService.log("Uploaded document; got updated doc info:", updatedDocInfo);
            let {fieldResponseId, newDocumentId, newDocumentUuid} = updatedDocInfo;
            if (!(fieldResponseId in uploadedDocumentIdsByFieldResponseId)) {
              uploadedDocumentIdsByFieldResponseId[fieldResponseId] = [];
              uploadedDocumentUuidsByFieldResponseId[fieldResponseId] = [];
            }
            uploadedDocumentIdsByFieldResponseId[fieldResponseId].push(newDocumentId);
            if (newDocumentUuid) {
              uploadedDocumentUuidsByFieldResponseId[fieldResponseId].push(newDocumentUuid);
            }
          })
      );

    return this.$q.all([
      this.$q.all(documentUploadPromises),
      this.$q.when(this.gpsPromise),
    ])
      .then(([_, gpsCoords]) => {
        this._updateRecapResponseWithUploadedDocuments(uploadedDocumentIdsByFieldResponseId, uploadedDocumentUuidsByFieldResponseId);

        if (gpsCoords) {
          this.recapResponseData.end_lat = gpsCoords.end_lat;
          this.recapResponseData.end_lon = gpsCoords.end_lon;
        }

        return this.APIService.post(createPartialResponseSetUrl, this.recapResponseData);
      })
      .catch(response => {
        this.LogService.error("Error saving recap response. Serialized response data:", this.recapResponseData, "error response:", response);
        return this.$q.reject(createError({
          error: 'ErrorSavingRecapResponse',
          message: 'Error saving recap response',
          response,
        }))
      })
      .then(response => {
        this.LogService.info(`Ignoring response from PartialRecapResponseSaveTask`);
        return response;
      })
      .catch(err => {
        this.LogService.error("Error creating recap response after saving:", err);
        return this.$q.reject(err);
      });
  }
  /**
   * Private methods
   */

  _serializeDocumentsForUpload(recapResponse) {
    // Find all documents pending upload
    const fieldResponses = recapResponse.collectAllVisibleFieldResponses();
    const fieldResponsesWithDocuments = fieldResponses.filter(fieldResponse => fieldResponse.documentsToUpload.length > 0);
    const serializedDocumentsForUploadByField = fieldResponsesWithDocuments.map(fieldResponse => {
      const documents = fieldResponse.documentsToUpload;
      return documents.map(document => this._serializeDocumentForUpload(document, fieldResponse, recapResponse));
    });
    return serializedDocumentsForUploadByField.reduce((pre, cur) => pre.concat(cur), []);
  }

  _serializeDocumentForUpload(document, fieldResponse, recapResponse) {
    const uploadPath = uploadRecapResponseFile
    const params = {
      events_custom_id: fieldResponse && fieldResponse.events_custom_id || null,
      events_id: recapResponse.eventId || null,
    }
    let uploadUrl = this.APIService.convertPathToURL(uploadPath, params)
    return {
      documentData: this.DocumentService.serialize(document),
      url: uploadUrl,
      fieldResponseId: fieldResponse.id,
    }
  }

  _uploadSerializedDocument(docInfo) {
    let { documentData, fieldResponseId, url } = docInfo
    return this.$q.when(this.DocumentService.create(documentData))
      .then(document => {
        return this.DocumentService.upload(document, url, null, 'file')
          .then(result => {
            let data = result.data
            let newDocumentId = data.id
            this._documentWasUploaded(docInfo, newDocumentId)
            let indexToRemove = this.serializedDocumentsToUpload.findIndex((doc) => doc.documentData.id == document.id )
            // Stopgap, but this should be present in the array
            if (indexToRemove > -1) {
              this.serializedDocumentsToUpload.splice(indexToRemove, 1)
            }
            // Below removes the doc from the field response's state.
            // This prevents uploading multiples on successive background tasks.
            this.RecapResponseService.documentWasUploaded(this.recapResponse, fieldResponseId, documentData, data.id, data.uuid)
            return {...docInfo, newDocumentId, newDocumentUuid: data.uuid || null}
          })
          .catch(err => {
            this.LogService.error("Error uploading document. docInfo:", docInfo, "error:", err)
            return this.$q.reject(err)
          })
      })
  }

  _documentWasUploaded(docInfo, newDocumentId) {
    let fieldResponseId = docInfo.fieldResponseId;
    if (!(fieldResponseId in this._trackDocumentIdsForFieldResponses)) {
      this._trackDocumentIdsForFieldResponses[fieldResponseId] = [];
    }
    this._trackDocumentIdsForFieldResponses[fieldResponseId].push(newDocumentId);
  }

  /**
   * Updates recapResponseData in place to reflect new document ID's, and the uuids that let the
   * final save claim them
   * @param uploadedDocumentIdsByFieldResponseId
   * @param uploadedDocumentUuidsByFieldResponseId
   * @private
   */
  _updateRecapResponseWithUploadedDocuments(uploadedDocumentIdsByFieldResponseId, uploadedDocumentUuidsByFieldResponseId = {}) {
    this.LogService.log("_updateRecapResponseWithUploadedDocuments")
    this.LogService.log("Initial response:", JSON.parse(JSON.stringify(this.recapResponseData)));

    let allFieldResponses = this.recapResponseData.form_responses
      .map(form_response => form_response.field_responses)
      .reduce((pre, cur) => pre.concat(cur), []);

    allFieldResponses.forEach(fieldResponse => {
      if (fieldResponse.id in uploadedDocumentIdsByFieldResponseId) {
        // Mark document as uploaded
        if (fieldResponse.value && fieldResponse.value.indexOf('null') === -1) {
          fieldResponse.value += '|' + uploadedDocumentIdsByFieldResponseId[fieldResponse.id].join('|');
        } else {
          fieldResponse.value = uploadedDocumentIdsByFieldResponseId[fieldResponse.id].join('|');
        }
        fieldResponse.document_uuids = (fieldResponse.document_uuids || [])
          .concat(uploadedDocumentUuidsByFieldResponseId[fieldResponse.id] || []);
      }
    });
  }
}

export default PartialRecapResponseSaveTask;
